/* eslint-disable sort-keys/sort-keys-fix */
const clickModEvent = (caseType, value) => {
  const event = {
    underlyingAction: {
      case: caseType,
      value,
    },
  };
  try {
    window.webShellClient.analytics.trackAction(event, {
      eventVersion: 2,
    });
  } catch (error) {
    console.error(error);
  }
};

/* Card Clicked Events */

export const clickModCardClicked = analytics => {
  const {
    assetId,
    hashKey,
    // inCarousel, // TODO; bring this back in with a working value
    cardKey,
    collectionId,
    collectionItemCount,
    placementId,
    positionId = 0,
    threadKey,
  } = analytics;

  if (collectionItemCount && hashKey) {
    // Build collection object, conditionally including index if > 0
    // Proto3 with IGNORE_IF_UNPOPULATED will omit uint32 fields with value 0
    // positionId is 0-based, so first carousel (position 0) will have index field omitted
    const collection = {
      collectionId,
      itemCount: collectionItemCount,
      ...(positionId > 0 && { index: positionId }), // Grid position within the page (0-based)
    };

    clickModEvent('coreContentV4CollectionItemClicked', {
      actionKey: hashKey,
      cardKey,
      collectionItem: {
        collection,
        content: {
          underlyingContent: {
            case: 'cms',
            value: {
              actionKeys: [hashKey],
              assetId,
              cardKey,
              threadKey,
            },
          },
        },
        index: placementId, // Item position within the carousel (0-based)
      },
    });
  }
};

/* Banner Clicked Events */

export const clickModBannerClicked = (bannerClickEl, threadKey) => {
  if (!bannerClickEl?.dataset) {
    console.error(
      'Banner click analytics requires a valid element with dataset',
    );
    return;
  }

  const { bannerHashKey, actionHashKey, bannerId, bannerIndex, totalBanners } =
    bannerClickEl.dataset;

  const resolvedActionKey = actionHashKey || bannerHashKey;

  if (!resolvedActionKey || !bannerHashKey) {
    console.error(
      'Banner click analytics requires bannerHashKey in element dataset',
    );
    return;
  }

  // Build collection object, conditionally including index if > 0
  // Banners are always at grid position 0 (0-based, top of page)
  // Proto3 will omit the index field since 0 has IGNORE_IF_UNPOPULATED
  const collection = {
    collectionId: bannerId,
    // index: 0 is omitted (banners at top of page, 0-based)
    itemCount: Number(totalBanners) || 1,
  };

  clickModEvent('coreContentV4CollectionItemClicked', {
    actionKey: resolvedActionKey,
    cardKey: bannerHashKey,
    collectionItem: {
      collection,
      content: {
        underlyingContent: {
          case: 'cms',
          value: {
            actionKeys: [resolvedActionKey],
            cardKey: bannerHashKey,
            threadKey: threadKey || null,
          },
        },
      },
      index: Number(bannerIndex) || 0, // Which banner in the carousel
    },
  });
};

/* Local Nav Clicked Events */

export const clickModLocalNavClicked = (localNavClickEl, threadKey) => {
  if (!localNavClickEl?.dataset) {
    console.error(
      'Local nav click analytics requires a valid element with dataset',
    );
    return;
  }

  const { clickModLocalNav: hashKey, localNavId: collectionId } =
    localNavClickEl.dataset;

  if (!hashKey) {
    console.error(
      'Local nav click analytics requires clickModLocalNav in element dataset',
    );
    return;
  }

  clickModEvent('coreContentV4CollectionItemClicked', {
    actionKey: hashKey,
    cardKey: collectionId,
    collectionItem: {
      collection: {
        collectionId,
        itemCount: 1,
      },
      content: {
        underlyingContent: {
          case: 'cms',
          value: {
            actionKeys: [hashKey],
            cardKey: collectionId,
            threadKey: threadKey || null,
          },
        },
      },
      index: 0,
    },
  });
};

/* Video Specific Events */

export const VideoClickableEnum = Object.freeze({
  VIDEO_CLICKABLE_ITEM_ENTER_FULLSCREEN: 6,
  VIDEO_CLICKABLE_ITEM_MUTE: 3,
  VIDEO_CLICKABLE_ITEM_PAUSE: 2,
  VIDEO_CLICKABLE_ITEM_PLAY: 1,
  VIDEO_CLICKABLE_ITEM_REPLAY: 5,
  VIDEO_CLICKABLE_ITEM_UNMUTE: 4,
});

export const clickModVideoClickEvent = eventType => {
  if (!Object.values(VideoClickableEnum).includes(eventType)) {
    console.error('Invalid video event type');
    return;
  }
  clickModEvent('coreContentV2VideoItemClicked', {
    item: eventType,
  });
};

/* Video Specific Events End */

export const UserExperienceViewedEnum = Object.freeze({
  // Backwards compatible keys
  CATEGORY_UNSPECIFIED: 0,
  editorial: 1,
  homepage: 2,
  'landing page': 3,
  sizechart: 4,
  // Standard protobuf naming conventions
  CATEGORY_EDITORIAL: 1,
  CATEGORY_HOMEPAGE: 2,
  CATEGORY_LANDING_PAGE: 3,
  CATEGORY_SIZE_FIT: 4,
});

export const clickModPageViewedEvent = (category, pageId) => {
  if (!pageId || typeof pageId !== 'string' || pageId.trim() === '') {
    console.error('Page ID is required');
    return;
  }
  if (!Object.values(UserExperienceViewedEnum).includes(category)) {
    console.error('Invalid landing page type');
    return;
  }
  clickModEvent('uxContentLandingV3UserExperienceViewed', {
    collection: {
      category,
      collectionId: pageId,
    },
  });
};

export const collectionItemViewedEvent = value => {
  clickModEvent('coreContentV3CollectionItemViewed', value);
};

/* Temporary Action Events */

const TEMP_ACTION_TYPE = Object.freeze({
  VIEW: 1,
  CLICK: 2,
  CUSTOM: 3,
});

export const clickModTemporaryAction = (actionName, type, data) => {
  if (!actionName || typeof actionName !== 'string') {
    console.error('actionName is required for temporary action');
    return;
  }
  const value = { actionName, type };
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    value.data = data;
  }
  clickModEvent('temporaryAction', value);
};

export const trackTemporaryView = (actionName, data) => {
  clickModTemporaryAction(actionName, TEMP_ACTION_TYPE.VIEW, data);
};

export const trackTemporaryClick = (actionName, data) => {
  clickModTemporaryAction(actionName, TEMP_ACTION_TYPE.CLICK, data);
};
